import { useEffect } from "react";
import { Card } from "@libs/zapi-ui/shadcn";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { Plus, Trash2 } from "lucide-react";
import { AppButton, Typography } from "@libs/zapi-ui/custom";
import { useNavigate } from "react-router-dom";
import { getRouteCreateProject } from "@/zapi-frontend/shared/const/router";
import { useGetProjectsList } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { CommitHash } from "@/zapi-frontend/shared/ui/custom/CommitHash";
import { updateCustomSnippetsUrl } from "@/zapi-frontend/pages/SettingsPage/services/updateCustomSnippetsUrl";
import { useMutation } from "@tanstack/react-query";
import { defineIsInvalidUrlError } from "../lib/defineIsInvalidUrlError";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { ProjectsTable } from "./projects-table";

export function ProjectsPage() {
  const { setProjects, projects } = useProjectStore();
  const navigate = useNavigate();
  const { setMe } = useMeStore();

  const { data, isLoading: isLoadingProjects, error, refetch } = useGetProjectsList();

  const mutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: () => {
      setMe({ customSnippetsUrl: null });
      refetch();
    }
  });

  const isLoading = isLoadingProjects || mutation.isPending;
  const isError = Boolean(error);

  const isInvalidUrlError = defineIsInvalidUrlError(error);

  const handleClear = () => {
    mutation.mutate({ customSnippetsUrl: null });
  };

  useEffect(() => {
    if (!data) return;
    setProjects(data);
  }, [data, setProjects]);

  return (
    <div className="min-h-[calc(100vh-56px)] flex flex-col">
      <div className="p-4 flex-1">
        <div className="flex justify-between items-center pb-4">
          <div className="flex items-center gap-4">
            <Typography variant="heading5" data-testid="projects-page-heading">
              Projects
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <AppButton
              data-testid="projects-page-create-project"
              onClick={() => navigate(getRouteCreateProject() + "?reset=true")}
            >
              <Plus className="w-4 h-4" />
              Project
            </AppButton>
          </div>
        </div>

        {isError && !isInvalidUrlError && !isLoading && (
          <div className="flex justify-center items-center pt-[180px]">
            <p className="text-sm text-zinc-500 font-[400]">Unexpected error occured</p>
          </div>
        )}

        {isInvalidUrlError && !isLoading && (
          <div className="flex flex-col justify-center items-center align-center gap-2 pt-[180px] ">
            <p className="text-sm text-zinc-500 font-[400] text-center">
              Template is configured to an inaccessible server. <br /> Please clear the URL and try again.
            </p>
            <AppButton
              size="sm"
              appearance="outlined"
              onClick={handleClear}
              disabled={mutation.isPending}
              className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </AppButton>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
          </div>
        )}

        {!isError && !isLoading && (
          <div>
            {projects.length === 0 && (
              <div className="flex justify-center items-center">
                <p className="text-sm text-zinc-500 font-[400] pt-[180px]">You currently don’t have any projects</p>
              </div>
            )}
            <Card className="py-0 rounded-[10px]">{projects.length > 0 && <ProjectsTable tableData={projects} />}</Card>
          </div>
        )}
      </div>
      <CommitHash />
    </div>
  );
}
